import React from 'react';
import {
  Drawer, Box, Typography, IconButton, Button, TextField,
  FormControl, InputLabel, Select, MenuItem, Divider,
  ToggleButton, ToggleButtonGroup, alpha, Chip,
} from '@mui/material';
import {
  Close, FilterList, Refresh, Search,
  TrendingUp, DirectionsCar, ExitToApp,
} from '@mui/icons-material';

const PRIMARY = '#282626';
const ORANGE  = '#F59E0B';

const ESTADO_OPTIONS = [
  { value: 'all',    label: 'Todos',           icon: <FilterList sx={{ fontSize: 16 }} /> },
  { value: 'active', label: 'Dentro',          icon: <DirectionsCar sx={{ fontSize: 16 }} /> },
  { value: 'exited', label: 'Salieron',         icon: <ExitToApp sx={{ fontSize: 16 }} /> },
];

export default function FilterDrawer({
  open, onClose,
  searchTerm, setSearchTerm,
  fechaDesde, setFechaDesde,
  fechaHasta, setFechaHasta,
  filter, setFilter,
  onSearch, onReset,
  // sucursalesDisponibles: null/undefined = ocultar el filtro (usuario con
  // sucursal fija, nada que elegir); [] o con datos = mostrarlo (modo global).
  sucursalFiltro, setSucursalFiltro, sucursalesDisponibles,
  calzaFiltro, setCalzaFiltro,
}) {
  const hasFilters = searchTerm || filter !== 'all' || fechaDesde || fechaHasta || sucursalFiltro || calzaFiltro;
  const mostrarFiltroSucursal = sucursalesDisponibles != null;

  const handleSearch = () => { onSearch(); onClose(); };
  const handleReset  = () => { onReset(); };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 340,
          bgcolor: '#FAFAFA',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Box sx={{
        bgcolor: PRIMARY, px: 3, py: 2,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <FilterList sx={{ color: 'white' }} />
          <Typography fontWeight={700} sx={{ color: 'white' }}>Filtros avanzados</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: 'white' } }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      {/* Filtros activos */}
      {hasFilters && (
        <Box sx={{ px: 2, py: 1.5, bgcolor: alpha(ORANGE, 0.08), borderBottom: '1px solid #E5E5E5' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" fontWeight={700} sx={{ color: '#D97706', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Filtros activos
            </Typography>
            <Button size="small" startIcon={<Refresh sx={{ fontSize: 14 }} />} onClick={handleReset}
              sx={{ fontSize: 11, color: '#D97706', textTransform: 'none', p: 0, minWidth: 0, fontWeight: 600 }}>
              Limpiar todo
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {searchTerm && <Chip size="small" label={`"${searchTerm}"`} onDelete={() => setSearchTerm('')} sx={{ height: 22, fontSize: 10 }} />}
            {filter !== 'all' && <Chip size="small" label={filter === 'active' ? 'Dentro' : 'Salieron'} onDelete={() => setFilter('all')} sx={{ height: 22, fontSize: 10 }} />}
            {fechaDesde && <Chip size="small" label={`Desde: ${fechaDesde}`} onDelete={() => setFechaDesde('')} sx={{ height: 22, fontSize: 10 }} />}
            {fechaHasta && <Chip size="small" label={`Hasta: ${fechaHasta}`} onDelete={() => setFechaHasta('')} sx={{ height: 22, fontSize: 10 }} />}
            {sucursalFiltro && (
              <Chip size="small"
                label={`Sucursal: ${sucursalesDisponibles?.find(s => String(s.id) === String(sucursalFiltro))?.nombre || sucursalFiltro}`}
                onDelete={() => setSucursalFiltro('')} sx={{ height: 22, fontSize: 10 }} />
            )}
            {calzaFiltro && <Chip size="small" label={`Calza: "${calzaFiltro}"`} onDelete={() => setCalzaFiltro('')} sx={{ height: 22, fontSize: 10 }} />}
          </Box>
        </Box>
      )}

      {/* Contenido */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5 }}>

        {/* Búsqueda por placa */}
        <Typography variant="caption" fontWeight={700} sx={{ color: PRIMARY, textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1 }}>
          Búsqueda rápida
        </Typography>
        <TextField
          fullWidth
          placeholder="Placa, color, tipo, observaciones..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          size="small"
          InputProps={{ startAdornment: <Search sx={{ mr: 1, color: '#999', fontSize: 18 }} /> }}
          sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }}
        />

        <Divider sx={{ mb: 3 }} />

        {/* Calza / puesto físico */}
        <Typography variant="caption" fontWeight={700} sx={{ color: PRIMARY, textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1 }}>
          Calza / puesto
        </Typography>
        <TextField
          fullWidth
          placeholder="Ej. 43"
          value={calzaFiltro}
          onChange={(e) => setCalzaFiltro(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          size="small"
          sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }}
        />

        <Divider sx={{ mb: 3 }} />

        {/* Estado */}
        <Typography variant="caption" fontWeight={700} sx={{ color: PRIMARY, textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1.5 }}>
          Estado del vehículo
        </Typography>
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_, v) => v && setFilter(v)}
          fullWidth
          sx={{ mb: 3 }}
        >
          {ESTADO_OPTIONS.map(o => (
            <ToggleButton key={o.value} value={o.value} size="small"
              sx={{
                flex: 1, textTransform: 'none', fontSize: 12, fontWeight: 600, gap: 0.5,
                '&.Mui-selected': { bgcolor: PRIMARY, color: 'white', '&:hover': { bgcolor: '#1a1a1a' } },
              }}>
              {o.icon}{o.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Divider sx={{ mb: 3 }} />

        {/* Sucursal — solo en modo global, con sucursal fija no hay nada que elegir */}
        {mostrarFiltroSucursal && (
          <>
            <Typography variant="caption" fontWeight={700} sx={{ color: PRIMARY, textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1.5 }}>
              Sucursal
            </Typography>
            <FormControl fullWidth size="small" sx={{ mb: 3 }}>
              <InputLabel>Sucursal</InputLabel>
              <Select
                label="Sucursal"
                value={sucursalFiltro || ''}
                onChange={(e) => setSucursalFiltro(e.target.value)}
                sx={{ borderRadius: 2, bgcolor: 'white' }}
              >
                <MenuItem value="">Todas las sucursales</MenuItem>
                {sucursalesDisponibles.map(s => (
                  <MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Divider sx={{ mb: 3 }} />
          </>
        )}

        {/* Rango de fechas */}
        <Typography variant="caption" fontWeight={700} sx={{ color: PRIMARY, textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1.5 }}>
          Rango de fechas
        </Typography>

        {/* Accesos rápidos */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {[
            { label: 'Hoy', fn: () => { const t = new Date().toISOString().split('T')[0]; setFechaDesde(t); setFechaHasta(t); } },
            { label: 'Esta semana', fn: () => { const t = new Date(); const d = new Date(t); d.setDate(t.getDate() - t.getDay()); setFechaDesde(d.toISOString().split('T')[0]); setFechaHasta(t.toISOString().split('T')[0]); } },
            { label: 'Este mes', fn: () => { const t = new Date(); setFechaDesde(`${t.getFullYear()}-${String(t.getMonth()+1).padStart(2,'0')}-01`); setFechaHasta(t.toISOString().split('T')[0]); } },
            { label: 'Último mes', fn: () => { const t = new Date(); t.setMonth(t.getMonth()-1); setFechaDesde(t.toISOString().split('T')[0]); setFechaHasta(new Date().toISOString().split('T')[0]); } },
          ].map(a => (
            <Button key={a.label} size="small" variant="outlined" onClick={a.fn}
              sx={{ borderRadius: 5, textTransform: 'none', fontSize: 11, fontWeight: 600,
                borderColor: '#DDD', color: PRIMARY, '&:hover': { borderColor: ORANGE, color: ORANGE } }}>
              {a.label}
            </Button>
          ))}
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <TextField
            fullWidth label="Desde" type="date" value={fechaDesde}
            onChange={(e) => setFechaDesde(e.target.value)}
            InputLabelProps={{ shrink: true }} size="small"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }}
          />
          <TextField
            fullWidth label="Hasta" type="date" value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)}
            InputLabelProps={{ shrink: true }} size="small"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }}
          />
        </Box>
      </Box>

      {/* Acciones */}
      <Box sx={{ p: 2.5, borderTop: '1px solid #E5E5E5', display: 'flex', gap: 1.5 }}>
        <Button fullWidth variant="outlined" onClick={handleReset}
          startIcon={<Refresh />}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, borderColor: '#DDD', color: '#666' }}>
          Limpiar
        </Button>
        <Button fullWidth variant="contained" onClick={handleSearch}
          startIcon={<Search />}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, bgcolor: PRIMARY, '&:hover': { bgcolor: '#1a1a1a' } }}>
          Aplicar filtros
        </Button>
      </Box>
    </Drawer>
  );
}
